import React, { useState, useEffect } from 'react';
import { Briefcase, MapPin, Building2, Clock, ExternalLink, FileText, TrendingUp, Search,
  MessageCircle, Video, ChevronRight, CheckCircle2, Loader2, AlertCircle,
  Copy, Users, DollarSign, Target, Lightbulb, Globe } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'jd', label: 'JD Intelligence', icon: FileText, color: 'indigo' },
  { id: 'mi', label: 'Market Intelligence', icon: TrendingUp, color: 'sky' },
  { id: 'sourcing', label: 'Smart Sourcing', icon: Search, color: 'violet' },
  { id: 'discovery', label: 'Discovery Questions', icon: MessageCircle, color: 'amber' },
  { id: 'interviews', label: 'AI Interviews', icon: Video, color: 'emerald' },
];

const colorMap = {
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-700', active: 'bg-indigo-600 text-white', ring: 'ring-indigo-200' },
  sky: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700', badge: 'bg-sky-100 text-sky-700', active: 'bg-sky-600 text-white', ring: 'ring-sky-200' },
  violet: { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-700', active: 'bg-violet-600 text-white', ring: 'ring-violet-200' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', active: 'bg-amber-600 text-white', ring: 'ring-amber-200' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', active: 'bg-emerald-600 text-white', ring: 'ring-emerald-200' },
};

const ShareButton = ({ url, label }) => {
  if (!url) return null;
  const copy = () => { navigator.clipboard.writeText(url); };
  return (
    <div className="flex items-center gap-2 mt-4 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
      <Globe className="h-4 w-4 text-slate-400 shrink-0" />
      <span className="text-xs text-slate-500 shrink-0">{label}:</span>
      <span className="text-xs font-mono text-indigo-600 truncate flex-1">{url}</span>
      <button onClick={copy} className="text-slate-400 hover:text-slate-600 shrink-0"><Copy className="h-3.5 w-3.5" /></button>
      <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 shrink-0"><ExternalLink className="h-3.5 w-3.5" /></a>
    </div>
  );
};

// ─── Tab Content Components ─────────────────────────────────────────

const JDTab = ({ data, shareUrl }) => {
  if (!data) return <PendingState label="JD Intelligence is being generated..." />;
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-2">Role Summary</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{data.role_summary}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-2">Mandatory Skills</h4>
          <div className="flex flex-wrap gap-1.5">
            {(data.mandatory_skills || []).map((s, i) => (
              <span key={i} className="text-xs bg-white border border-indigo-200 text-indigo-700 px-2.5 py-1 rounded-lg font-medium">{s}</span>
            ))}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Nice to Have</h4>
          <div className="flex flex-wrap gap-1.5">
            {(data.nice_to_have_skills || []).map((s, i) => (
              <span key={i} className="text-xs bg-white border border-slate-200 text-slate-600 px-2.5 py-1 rounded-lg">{s}</span>
            ))}
          </div>
        </div>
      </div>
      {data.key_responsibilities?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Key Responsibilities</h4>
          <div className="space-y-1.5">
            {data.key_responsibilities.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <ChevronRight className="h-3.5 w-3.5 text-indigo-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-700">{r}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {data.experience_range && (
          <div className="bg-white border rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Experience</p>
            <p className="text-sm font-semibold text-slate-900">{data.experience_range}</p>
          </div>
        )}
        {data.education && (
          <div className="bg-white border rounded-lg p-3">
            <p className="text-[10px] text-slate-400 uppercase font-semibold">Education</p>
            <p className="text-sm font-semibold text-slate-900">{data.education}</p>
          </div>
        )}
      </div>
      {data.interview_focus_areas?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Interview Focus</h4>
          <div className="flex flex-wrap gap-1.5">
            {data.interview_focus_areas.map((a, i) => (
              <span key={i} className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-700 px-2.5 py-1 rounded-lg font-medium">{a}</span>
            ))}
          </div>
        </div>
      )}
      <ShareButton url={shareUrl} label="Public JD Report" />
    </div>
  );
};

const MITab = ({ data, shareUrl }) => {
  if (!data) return <PendingState label="Market Intelligence report is being generated..." />;
  const sal = data.salary_benchmarks || {};
  return (
    <div className="space-y-5">
      {data.market_overview && (
        <div>
          <h3 className="text-base font-semibold text-slate-900 mb-2">Market Overview</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{data.market_overview}</p>
        </div>
      )}
      {Object.keys(sal).length > 0 && (
        <div className="bg-sky-50/50 border border-sky-100 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-sky-700 uppercase tracking-wider mb-3 flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Salary Benchmarks</h4>
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(sal).map(([k, v]) => (
              <div key={k} className="text-center bg-white border border-sky-200 rounded-lg p-3">
                <p className="text-[10px] text-sky-500 uppercase font-semibold">{k}</p>
                <p className="text-lg font-bold text-slate-900">{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.talent_supply && (
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(data.talent_supply).map(([k, v]) => (
            <div key={k} className="bg-white border rounded-lg p-3 text-center">
              <p className="text-[10px] text-slate-400 uppercase font-semibold">{k.replace(/_/g, ' ')}</p>
              <p className="text-sm font-semibold text-slate-900 mt-1">{v}</p>
            </div>
          ))}
        </div>
      )}
      {data.competing_companies?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Competing Companies</h4>
          <div className="flex flex-wrap gap-1.5">
            {data.competing_companies.map((c, i) => <span key={i} className="text-xs bg-red-50 border border-red-200 text-red-700 px-2.5 py-1 rounded-lg font-medium">{c}</span>)}
          </div>
        </div>
      )}
      {data.market_trends?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Lightbulb className="h-3.5 w-3.5" /> Market Trends</h4>
          <div className="space-y-1.5">
            {data.market_trends.map((t, i) => (
              <div key={i} className="flex items-start gap-2 bg-amber-50/50 border border-amber-100 rounded-lg p-2.5">
                <TrendingUp className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700">{typeof t === 'string' ? t : t.trend || JSON.stringify(t)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      <ShareButton url={shareUrl} label="Public MI Report" />
    </div>
  );
};

const SourcingTab = ({ data, shareUrl }) => {
  if (!data) return <PendingState label="Smart Sourcing strategy is being generated..." />;
  return (
    <div className="space-y-5">
      {data.sourcing_strategy && <p className="text-sm text-slate-600 leading-relaxed">{data.sourcing_strategy}</p>}
      {data.channels?.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Sourcing Channels</h4>
          <div className="space-y-2">
            {data.channels.map((ch, i) => (
              <div key={i} className="flex items-center gap-4 bg-white border rounded-xl px-4 py-3">
                <Target className="h-4 w-4 text-violet-500 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{ch.name || ch}</p>
                  {ch.expected_response_rate && <p className="text-xs text-slate-500">Response rate: {ch.expected_response_rate}</p>}
                </div>
                {ch.priority && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ch.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{ch.priority}</span>}
                {ch.cost && <span className="text-xs text-slate-400">{ch.cost}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      {data.timeline && (
        <div className="bg-violet-50/50 border border-violet-100 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-violet-700 uppercase tracking-wider mb-1">Timeline</h4>
          <p className="text-sm text-slate-700">{data.timeline}</p>
        </div>
      )}
      <ShareButton url={shareUrl} label="Public Sourcing Report" />
    </div>
  );
};

const DiscoveryTab = ({ data, shareUrl }) => {
  if (!data) return <PendingState label="Discovery questions are being generated..." />;
  const sections = [
    { key: 'technical_questions', label: 'Technical Questions', icon: FileText, color: 'indigo' },
    { key: 'behavioral_questions', label: 'Behavioral Questions', icon: Users, color: 'amber' },
    { key: 'culture_fit_questions', label: 'Culture Fit', icon: MessageCircle, color: 'emerald' },
  ];
  return (
    <div className="space-y-5">
      {sections.map(sec => {
        const questions = data[sec.key];
        if (!questions?.length) return null;
        const Icon = sec.icon;
        return (
          <div key={sec.key}>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Icon className="h-3.5 w-3.5" /> {sec.label}
            </h4>
            <div className="space-y-2">
              {questions.map((q, i) => (
                <div key={i} className="flex items-start gap-3 bg-white border rounded-xl px-4 py-3">
                  <span className={`w-6 h-6 rounded-full bg-${sec.color}-100 text-${sec.color}-700 text-[10px] font-bold flex items-center justify-center shrink-0`}>{i + 1}</span>
                  <p className="text-sm text-slate-700 leading-relaxed">{typeof q === 'string' ? q : q.question || JSON.stringify(q)}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      <ShareButton url={shareUrl} label="Public Discovery Report" />
    </div>
  );
};

const InterviewsTab = ({ interviews }) => {
  if (!interviews?.length) return (
    <div className="text-center py-12">
      <Video className="h-10 w-10 mx-auto text-slate-300 mb-3" />
      <p className="text-sm text-slate-500">No AI interview links created yet.</p>
      <p className="text-xs text-slate-400 mt-1">Use the API to create interview links for candidates.</p>
    </div>
  );
  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500 mb-3">{interviews.length} interview link(s) created</p>
      {interviews.map((iv, i) => (
        <div key={i} className="flex items-center gap-4 bg-white border rounded-xl px-4 py-3">
          <Video className="h-4 w-4 text-emerald-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900">{iv.candidate_name}</p>
            <p className="text-xs text-slate-400 font-mono truncate">{iv.url}</p>
          </div>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
            iv.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
            iv.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
            'bg-slate-100 text-slate-600'
          }`}>{iv.status}</span>
          <a href={iv.url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800"><ExternalLink className="h-4 w-4" /></a>
        </div>
      ))}
    </div>
  );
};

const PendingState = ({ label }) => (
  <div className="text-center py-16">
    <div className="relative mx-auto w-12 h-12 mb-4">
      <Loader2 className="h-12 w-12 text-indigo-200 animate-spin absolute" />
      <AlertCircle className="h-5 w-5 text-indigo-600 absolute top-3.5 left-3.5" />
    </div>
    <p className="text-sm font-medium text-slate-700">{label}</p>
    <p className="text-xs text-slate-400 mt-1">This page will auto-refresh when ready.</p>
  </div>
);

// ─── Main Embed Page ────────────────────────────────────────────────
export default function EmbedPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('jd');

  const token = window.location.pathname.split('/embed/')[1];

  useEffect(() => {
    if (!token) { setError('Invalid embed link'); setLoading(false); return; }
    const fetchData = async () => {
      try {
        const res = await fetch(`${API}/api/v1/embed/${token}`);
        if (!res.ok) { setError('This link is invalid or has expired.'); return; }
        setData(await res.json());
      } catch (e) { setError('Failed to load data.'); }
      finally { setLoading(false); }
    };
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="text-center">
        <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-3" />
        <p className="text-base font-medium text-slate-900">{error}</p>
      </div>
    </div>
  );

  const job = data?.job || {};
  const tc = colorMap[TABS.find(t => t.id === activeTab)?.color || 'indigo'];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-sm">H</span>
              </div>
              <div>
                <p className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">HireFast AI</p>
                <h1 className="text-lg font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="embed-job-title">{job.title}</h1>
              </div>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-500">
              {job.client_name && <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{job.client_name}</span>}
              {job.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{job.city}{job.country ? `, ${job.country}` : ''}</span>}
              {job.job_type && <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{job.job_type}</span>}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Tab Navigation */}
        <nav className="flex gap-1 bg-white border border-slate-200 rounded-2xl p-1.5 mb-6 shadow-sm" data-testid="embed-tabs">
          {TABS.map(tab => {
            const active = activeTab === tab.id;
            const Icon = tab.icon;
            const c = colorMap[tab.color];
            const hasData = tab.id === 'jd' ? !!data?.jd_analysis :
              tab.id === 'mi' ? !!data?.market_intelligence :
              tab.id === 'sourcing' ? !!data?.sourcing :
              tab.id === 'discovery' ? !!data?.discovery :
              tab.id === 'interviews' ? true : false;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all
                  ${active ? `${c.active} shadow-sm` : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                data-testid={`embed-tab-${tab.id}`}>
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{tab.label}</span>
                {!hasData && tab.id !== 'interviews' && <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />}
              </button>
            );
          })}
        </nav>

        {/* Tab Content */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="embed-content">
          {activeTab === 'jd' && <JDTab data={data?.jd_analysis} shareUrl={data?.share_links?.jd} />}
          {activeTab === 'mi' && <MITab data={data?.market_intelligence} shareUrl={data?.share_links?.mi} />}
          {activeTab === 'sourcing' && <SourcingTab data={data?.sourcing} shareUrl={data?.share_links?.sourcing} />}
          {activeTab === 'discovery' && <DiscoveryTab data={data?.discovery} shareUrl={data?.share_links?.discovery} />}
          {activeTab === 'interviews' && <InterviewsTab interviews={data?.interviews} />}
        </div>

        {/* Footer */}
        <footer className="text-center py-6 mt-4">
          <p className="text-[10px] text-slate-400">Powered by HireFast AI — TekWissen Staffing Solutions</p>
        </footer>
      </div>
    </div>
  );
}
